import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { BadgeSectionTypes, CurrentUser, CustomerEventCounterData, MenuItemType } from 'types';
import { MENU_ADMIN_ITEMS, MENU_GUEST_ITEMS, MENU_USER_ITEMS } from 'consts';
import { getEventCounters } from 'api';
import { checkIfSignUpIncomplete, checkIsAdmin, goTo, prepareRedirectPath } from 'utils/services';
import { getDataFromLocalStore, removeLogInDataFromBrowser, setDataToLocalStore } from 'utils/localStoreTools';
import { isEmpty } from 'utils/toggleParams';
import { NextIconWithNotification } from 'components/NextImage/NextIconWithNotification';
import { NextLink } from 'components/NextLink';
import { NextIcon } from 'components/NextImage';
import { Button } from 'components/Button';
import { Burger } from 'components/Burger';
import { AccountAvatar } from 'components/AccountAvatar';
import { MainMenu } from 'components/MainMenu';
import { SideMenuItem } from 'components/SideMenu';
import { getManuItemMap, mainHeaderData } from './mainHeaderData';

import styles from './MainHeader.module.scss';
import NotificationsModal from './NotificationsModal';
import { HeaderSearch } from '../SearchComponents/HeaderSearch';
import Image from 'next/legacy/image';
import cx from 'classnames';

export type MainHeaderProps = {
  badge?: BadgeSectionTypes;
  changeOpened?: () => void;
  rightItems?: any;
  searchItems?: any;
  user?: CurrentUser | null;
  children?: React.ReactElement;
  type?: string;
};

export const MainHeader = ({ badge = {} as BadgeSectionTypes, changeOpened, user, type }: MainHeaderProps) => {
  const router = useRouter();
  const guestCartToken = getDataFromLocalStore('guestCartToken');
  const [mainMenuOpened, setMainMenuOpened] = useState(false);
  const [eventCounterData, setEventCounterData] = useState<CustomerEventCounterData>({} as CustomerEventCounterData);
  const isAuthorized = !isEmpty(user);

  const isLoggedIn = !!user?.email;
  const isSignUpIncomplete = checkIfSignUpIncomplete(user);
  const [crowdBadgeClosed, setCrowdBadgeClosed] = useState(true) // useState(getDataFromLocalStore('crowd_badge_closed'));

  const isAdmin = checkIsAdmin(user);
  const {
    unread_messages_count: unreadMessages,
    unread_sell_orders: newSellOrders,
    unread_purchase_orders: newPurchaseOrders,
    unread_offers: newOffers,
    unread_notifications_count: unreadCount
  } = eventCounterData || {};

  useEffect(() => {
    trackEventCounter().then();
  }, []);

  const trackEventCounter = async () => {
    const eventCounterResponse = await getEventCounters();
    if (eventCounterResponse.ok) {
      setEventCounterData(eventCounterResponse);
    }
  };

  const trackEventCounterChangesByVisibility = () => {
    if (document.visibilityState === 'visible') {
      trackEventCounter().then();
    }
  };

  useEffect(() => {
    if (isAdmin) return;
    document.addEventListener('visibilitychange', trackEventCounterChangesByVisibility);
    return () => {
      if (isAdmin) return;
      document.removeEventListener('visibilitychange', trackEventCounterChangesByVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMainMenu = () => {
    setMainMenuOpened((opened) => !opened);
  };

  const logOut = () => {
    removeLogInDataFromBrowser();
    router.reload();
  };

  const closeMenu = () => {
    setMainMenuOpened(false);
  };

  const protectedRedirect = (router: any, path: string) => {
    closeMenu();
    if (!isLoggedIn) {
      goTo(router, `/sign-in?redirect=${path}`);
      return;
    }
    if (isSignUpIncomplete) {
      goTo(router, `/sign-up?redirect=${path}`);
      return;
    }
    goTo(router, path);
  };

  const addAdminPanelLinks = (): MenuItemType[] => {
    const links: MenuItemType[] = [];
    if ((!!user?.permissions && user.permissions.includes('dashboard_view')) || user?.role === 'super_admin') {
      links.push('dashboard');
    }
    return links;
  };

  const menuItemMap: Record<MenuItemType, SideMenuItem> = getManuItemMap({
    router,
    protectedRedirect,
    logOut,
    unreadMessages,
    newOffers,
    user,
    newPurchaseOrders,
    newSellOrders,
    closeMenu,
  });

  const getMenuItems = () => {
    if (!isLoggedIn) {
      return MENU_GUEST_ITEMS.map((key: MenuItemType) => menuItemMap[key]);
    }
    if (isAdmin) {
      return [...addAdminPanelLinks(), ...MENU_ADMIN_ITEMS].map((key: MenuItemType) => menuItemMap[key]);
    }
    return MENU_USER_ITEMS.filter(key => (key!=='rentalDashboard' || user?.account_type==='business')).map((key: MenuItemType) => menuItemMap[key]);
  };

  const onCloseCrowdBadge = () => {
    setDataToLocalStore('crowd_badge_closed', true);
    setCrowdBadgeClosed(true);
  };

  const renderBadge = () => (
    <div className={styles.badge} style={{ backgroundColor: badge.bg_color }}>
      {badge.title}
    </div>
  );

  const renderCrowdBadge = () => (
    <div className={styles.badge}>
      <div className={styles.miniBlock} />
      <span
        className={styles.text}
        onClick={() => router.push(`/crowdfunding`)}
      >Invest in SENDY on StartEngine – Be part of the adventure! 👉 <span className={styles.underline}>Invest Now</span></span>
      <div className={styles.miniBlock}>
        <NextIcon src="/icons/XWhite.svg" customClass={styles.closeIcon} onClick={onCloseCrowdBadge} />
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.headerBorder} />
      {(badge.is_enabled || !crowdBadgeClosed) && <div className={styles.badgeHeight} />}
      <div className={styles.container}>
        {crowdBadgeClosed ? (badge.is_enabled && renderBadge()) : renderCrowdBadge()}
        <div className={styles.header}>
          <div className={styles.leftSide}>
            <NextIcon
              customClass={styles.menuIcon}
              onClick={toggleMainMenu}
              src='/icons/office/ListDark.svg'
              size={32}
              alt='close'
            />
            <MainMenu active={mainMenuOpened} items={getMenuItems()} setActive={setMainMenuOpened} user={user}
                      isSignUpIncomplete={isSignUpIncomplete} />
            <div className={styles.burgerIcon} />
            <NextLink href='/' className={styles.logoLink} useDirectLink={router.route === '/'}>
              <NextIcon src='/icons/logo.svg' width={156} alt='Logo' customClass={styles.logo} />
            </NextLink>
          </div>

          <div className={styles.forDesktop}>
            <CentralHeader user={user} router={router} type={type} />
          </div>

          <div className={styles.rightSide}>
            {isAuthorized && (
              <NextLink
                href={'/my-favorites'}
              >
                <div className={styles.item}>
                  <NextIconWithNotification
                    src={'/icons/weather/StarDark.svg'}
                    alt={'Favorites'}
                  />
                </div>
              </NextLink>
            )}
            <NextLink
              href={`/cart${guestCartToken ? `?guestCartKey=${guestCartToken}` : ''}`}
            >
              <div className={styles.item}>
                <NextIconWithNotification
                  src={'/icons/commerce/ShoppingCartDark.svg'}
                  alt={'Cart'}
                />
              </div>
            </NextLink>
            {isAuthorized && (
              <>
                <NotificationsModal notification={unreadCount} />
                <div className={styles.accountIcon}>
                  <NextLink href={`/users/${user?.username}`}>
                    <AccountAvatar
                      sex={user?.sex || ''}
                      photo={user?.photo}
                      isAdmin={isAdmin}
                      customClass={styles.accountIcon}
                    />
                  </NextLink>
                </div>
              </>
            )}
            {!isAuthorized && (
              <div className={styles.signButtons}>
                <Button
                  id='header-sign-in'
                  buttonType={'white'}
                  caption='Sign In'
                  action={() => router.push(`/sign-in${prepareRedirectPath()}`)}
                />
                <Button
                  id='header-sign-up'
                  caption='Sign Up'
                  action={() => router.push(`/sign-up${prepareRedirectPath()}`)}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.forMobiles}>
          <CentralHeader user={user} router={router} type={type} />
        </div>
      </div>
    </>
  );
};


const CentralHeader = ({ user, router, type }: any) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  return (
    <div className={styles.centralContainer}>
      <div
        onClick={() => router.push(`/gear?type=sell`)}
        className={cx(styles.element, styles.buy, {[styles.active]: type==='sell'})}
      >Buy</div>
      <div
        onClick={() => router.push(`/selling`)}
        className={cx(styles.element, styles.sell, {[styles.active]: type==='selling'})}
      >Sell</div>
      <div
        onClick={() => router.push(`/gear?type=rent`)}
        className={cx(styles.element, styles.rent, {[styles.active]: type==='rent'})}
      >Rent</div>
      <div
        onClick={() => router.push(`/explore`)}
        className={cx(styles.element, styles.explore, {[styles.active]: type==='explore'})}
      >Explore</div>
      <div
        className={styles.elementSearch}
        style={{backgroundColor: isSearchFocused ? '#dfe3e5' : 'transparent'}}
      >
        {isSearchFocused && (
          <div style={{marginTop: 8, marginRight: 4}}>
            <Image alt="Image" src={'/icons/MagnifyingGlass.svg'} width={24} height={24} />
          </div>
        )}
        <HeaderSearch
          user={user}
          headerSearch={true}
          placeholder="Search gear & apparel"
          markFocused={(b) => { setIsSearchFocused(b) }}
          showQueryInUsers
          mobileLike
        />
      </div>
    </div>
  )
}
